<template lang="pug">
  management-page(title="Overview"
                  :creatable="false"
                  :searchable="false"
                  :showHeader="false")
    template(v-slot:body)
      .tiles-card
        .tiles-card-header
          aiq-button.export-tiles(@click="exportTiles" :disabled="loading")
            i.iq-ico-export
        .tiles-grid
          smart-assist-tile(v-for="tile in tiles"
                            :key="tile.key"
                            :title="tileTitle(tile)"
                            :category="tile.category"
                            :help-text="tileHelp(tile)"
                            :highlight="tile.highlight"
                            :value="tileValues[tile.key]"
                            :loading="loading")
      .chart-grid
        aiq-row(:gutter="16")
          aiq-col(:span="16")
            overview-chart(:dateRange="dateRangeUtc"
                           :chartConfig="dailyQueriesChartConfig"
                           :filters="filters"
                           visualization="aiq-bar-chart"
                           grouped
                           :formatXTick="formatChartXTick"
                           @onChartDataChange="onDailyQueriesDataChange"
                           @onXLabelsChange="dailyQueriesXLabels=$event"
                           :reportFileName="returnReportFileName('LynqAI Daily Question Volume')"
                           :shouldHandlePartialDates="true")
          aiq-col(:span="8")
            .donut-loading-area(v-loading="segmentLoading")
              overview-chart(:chartConfig="queriesBySegmentChartConfig"
                             :preLoadedchartData="queriesBySegmentChartData"
                             :getOriginalData="() => queriesBySegmentDownloadData"
                             visualization="aiq-donut-chart"
                             breakdown="none"
                             :downloadWithTimeTicks="false"
                             :reserveFootnoteSpace="true"
                             :style="{ height: '100%' }"
                             :reportFileName="returnReportFileName('LynqAI Questions by Segment')")
</template>

<script>
import ManagementPage from '@/components/ManagementPage/ManagementPage.vue';
import { get, find } from 'lodash';
import { downloadAsFile } from '@/libs';
import { dstDateRangeUtc } from '../../configs/dateRange';
import dayjs from '@/localPlugins/dayjs';
import OverviewChart from '@/components/Chart.vue';
import SmartAssistTile, { formatTileValue } from './SmartAssistTile.vue';
import { SMART_ASSIST_TILES } from './constants';
import { PERCENT_MULTIPLIER } from '../../constants';
import dailyQueriesChartConfig from '../../configs/lynqAiDailyQueries';
import lynqAiQueriesBySegmentConfig, { SEGMENT_DONUT_PALETTE } from '../../configs/lynqAiQueriesBySegment';

const I18N_TILES_PREFIX = 'metrics_tab.smart_assist_tab.overview_tab.tiles';
const OVERVIEW_LOAD_ERROR = 'metrics_tab.smart_assist_tab.overview_tab.errors.load_failed';

const LYNQ_AI_TILES = SMART_ASSIST_TILES.filter(
  tile => tile.key !== 'feedback' && tile.key !== 'feedback_rate',
);

const safePercent = (num, den) => (den > 0 ? (num / den) * PERCENT_MULTIPLIER : null);

export default {
  name: 'LynqAiOverview',
  pageResource: '/metrics',
  components: {
    ManagementPage,
    SmartAssistTile,
    OverviewChart,
  },
  props: {
    dateRange: { type: Array, required: true },
    filters: { type: Array, default: () => [] },
  },
  data() {
    return {
      tiles: LYNQ_AI_TILES,
      counts: {},
      loading: false,
      dailyQueriesChartConfig,
      dailyQueriesChartData: [],
      dailyQueriesXLabels: {},
      queriesBySegment: [],
      segmentLoading: false,
    };
  },
  computed: {
    dateRangeUtc() {
      return dstDateRangeUtc(this.dateRange);
    },
    tileValues() {
      const c = this.counts;
      const answered = get(c, 'answered_count', 0);
      const unanswered = get(c, 'unanswered_count', 0);
      const queries = get(c, 'query_count', null);
      const sessions = get(c, 'session_count', null);
      return {
        queries,
        answer_rate: safePercent(answered, answered + unanswered),
        users: get(c, 'user_count', null),
        sessions,
        queries_per_session: sessions > 0 ? queries / sessions : null,
      };
    },
    queriesBySegmentLegendItems() {
      return this.queriesBySegment.map((item, idx) => ({
        label: item.segment_name,
        displayName: `${item.segment_name} (${item.count})`,
        value: item.count,
        color: SEGMENT_DONUT_PALETTE[idx % SEGMENT_DONUT_PALETTE.length],
      }));
    },
    queriesBySegmentChartConfig() {
      const colColors = this.queriesBySegmentLegendItems.reduce((acc, item) => {
        acc[item.displayName] = item.color;
        return acc;
      }, {});
      // `lines` must be populated or Base.vue marks every legend item disabled.
      const lines = this.queriesBySegmentLegendItems.reduce((acc, item) => {
        acc[item.displayName] = { label: item.displayName };
        return acc;
      }, {});
      return {
        ...lynqAiQueriesBySegmentConfig,
        lines,
        graphParams: { ...lynqAiQueriesBySegmentConfig.graphParams, colColors },
      };
    },
    queriesBySegmentChartData() {
      return {
        dataset: [{
          data_points: this.queriesBySegmentLegendItems.map(item => ({
            name: item.displayName,
            value: item.value,
          })),
        }],
      };
    },
    queriesBySegmentDownloadData() {
      const total = this.queriesBySegmentLegendItems.reduce((sum, item) => sum + item.value, 0);
      return {
        dataset: [{
          data_points: this.queriesBySegmentLegendItems.map(item => ({
            name: item.label,
            value: item.value,
            percentage: formatTileValue('percent', safePercent(item.value, total), { localized: false }),
          })),
        }],
      };
    },
  },
  watch: {
    dateRangeUtc: {
      immediate: true,
      handler() {
        this.fetchOverview();
        this.fetchQueriesBySegment();
      },
    },
    filters() {
      this.fetchOverview();
      this.fetchQueriesBySegment();
    },
  },
  methods: {
    tileTitle(tile) {
      return this.$t(`${I18N_TILES_PREFIX}.${tile.key}.title`);
    },
    tileHelp(tile) {
      return this.$t(`${I18N_TILES_PREFIX}.${tile.key}.help_text`);
    },
    formatChartXTick(date) {
      const label = this.dailyQueriesXLabels[dayjs(date).valueOf()] || dayjs(date).format('MMM DD');
      const partialIndicator = this.isDailyQueriesDataPartial(date) ? '*' : '';
      return `${label}${partialIndicator}`;
    },
    isDailyQueriesDataPartial(date) {
      const timeStamp = new Date(date).getTime();
      return find(this.dailyQueriesChartData, { x: timeStamp })?.isPartial;
    },
    onDailyQueriesDataChange(data) {
      this.dailyQueriesChartData = get(data, 'dataset[0].data_points', []);
    },
    returnReportFileName(chartName = 'data') {
      const [startTimestamp, endTimestamp] = this.dateRange;
      const startDate = dayjs.isDayjs(startTimestamp)
        ? startTimestamp.format('YYYY-MM-DD')
        : dayjs(startTimestamp).format('YYYY-MM-DD');
      const endDate = dayjs.isDayjs(endTimestamp)
        ? endTimestamp.format('YYYY-MM-DD')
        : dayjs(endTimestamp).format('YYYY-MM-DD');
      return `${chartName.replaceAll(' ', '_')}_${startDate}_${endDate}`;
    },
    exportTiles() {
      const rows = this.tiles.map(tile => ({
        Metric: this.tileTitle(tile),
        Value: formatTileValue(tile.category, this.tileValues[tile.key], { localized: false }),
      }));
      downloadAsFile({
        filename: this.returnReportFileName('LynqAI Overview'),
        data: rows,
        dataType: 'table',
      });
    },
    metricsPeriod() {
      const [period_start, period_end] = this.dateRangeUtc;
      return period_start && period_end ? { period_start, period_end } : null;
    },
    async fetchOverview() {
      const period = this.metricsPeriod();
      if (!period) return;
      this.loading = true;
      try {
        const data = await this.$store.dispatch('metrics/getMetric', [
          'lynq_ai',
          { ...period, filters: this.filters },
        ]);
        this.counts = get(data, 'dataset', {});
      } catch (err) {
        this.$aiq.notify.error(this.$t(OVERVIEW_LOAD_ERROR));
        this.counts = {};
      } finally {
        this.loading = false;
      }
    },
    async fetchQueriesBySegment() {
      const period = this.metricsPeriod();
      if (!period) return;
      this.segmentLoading = true;
      try {
        const data = await this.$store.dispatch('metrics/getMetric', [
          'lynq_ai_by_segment',
          { ...period, bucket: 'day', filters: this.filters },
        ]);
        this.queriesBySegment = get(data, 'dataset.0.data_points', []);
      } catch (err) {
        this.$aiq.notify.error(this.$t(OVERVIEW_LOAD_ERROR));
        this.queriesBySegment = [];
      } finally {
        this.segmentLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../styles/aiq-variables.scss";

.donut-loading-area {
  position: relative;
  height: 100%;

  :deep(.el-loading-mask) {
    top: 96px;
    bottom: 40px;
    left: 8px;
    right: 8px;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 4px;
  }
}

.tiles-card {
  background: $aiq-bgc-list-item;
  border: 1px solid #e4e7ed;
  padding: 24px;
}

.tiles-card-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.export-tiles {
  height: 30px;
  width: 40px;
  padding: 0;
}

.tiles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}

.chart-grid {
  margin-top: 16px;
  margin-bottom: 16px;
}
</style>

<style lang="scss">
@import "../../../../styles/aiq-variables-dark.scss";

[data-theme="dark"] .tiles-card {
  background: $aiq-bgc-sidebar-item-hover;
  border-color: $aiq-bdc-input-default;
}
</style>
